if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector(
        '.js-results-container'
      );
      this.btnSubmit = this.querySelector('.js-btn-submit');
      this.btnClearText = this.querySelector('.js-btn-clear-text');
      this.errorMessageContainer = this.querySelector(
        '.js-message-error'
      );
      this.errorMessageText =
        this.errorMessageContainer.querySelector('[data-text]');
      this.timeout = null;
      this.isOpen = false;
      this.drawer = this.closest('.js-drawer');
      this.form = this.querySelector('form');

      this.input.addEventListener('input', e => {
        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
          const searchTerm = this.input.value.trim();

          if (searchTerm.length <= 0) {
            this.close();

            return;
          }

          this.isOpen = true;

          this.getSearchResults(searchTerm);
        }, 300);
      });

      this.input.addEventListener('keydown', e => {
        const isEscape = e.key === 'Escape';

        if (!isEscape) {
          return;
        }

        if (!this.input.value) {
          if (this.drawer) {
            this.drawer.parentElement.parentElement.toggleDrawer();
          }

          return;
        }

        this.close();
      });

      this.btnClearText.addEventListener('click', e => {
        this.close();
      });
    }

    getSearchTermVariants(searchTerm) {
      if (!searchTerm) return [];

      const variants = [searchTerm];

      const stripped = searchTerm
        .replace(/([A-Za-z])\.\s+(?=[A-Za-z])/g, '$1')
        .replace(/([A-Za-z])\.(?=[A-Za-z])/g, '$1');

      if (stripped && stripped !== searchTerm) {
        variants.push(stripped);
      }

      const lettersOnly = searchTerm.replace(/\s+/g, '');
      if (
        /^[A-Za-z]+$/.test(lettersOnly) &&
        lettersOnly.length >= 2 &&
        lettersOnly.length <= 8
      ) {
        const dottedUpper = lettersOnly
          .toUpperCase()
          .split('')
          .join('.');
        if (dottedUpper !== searchTerm) {
          variants.push(dottedUpper);
        }
      }

      return [...new Set(variants)];
    }

    async fetchPredictiveSearchHtml(searchTerm) {
      const url = new URL(
        `${Shopify.routes.root}search/suggest`,
        window.location.origin
      );

      url.searchParams.set('q', searchTerm);
      url.searchParams.set(
        'resources[type]',
        'product,collection,query'
      );
      url.searchParams.set('resources[limit]', '8');
      url.searchParams.set('section_id', 'predictive-search');

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(response.status);
      }

      return await response.text();
    }

    mergeResults(primaryHtml, secondaryHtml) {
      const parser = new DOMParser();
      const primarySection = parser
        .parseFromString(primaryHtml, 'text/html')
        .querySelector('#shopify-section-predictive-search');

      const secondarySection = parser
        .parseFromString(secondaryHtml, 'text/html')
        .querySelector('#shopify-section-predictive-search');

      if (!primarySection) return '';
      if (!secondarySection) return primarySection.innerHTML;

      const combined = primarySection.cloneNode(true);

      const primaryVendorsRow = combined.querySelector(
        '.predictive-search__row--vendors'
      );
      const secondaryVendorsRow = secondarySection.querySelector(
        '.predictive-search__row--vendors'
      );

      if (!primaryVendorsRow && secondaryVendorsRow) {
        const productsGrid = combined.querySelector(
          '.predictive-search__grid'
        );
        const productsRow = productsGrid
          ? productsGrid.closest('.predictive-search__row')
          : null;

        if (productsRow) {
          productsRow.parentElement.insertBefore(
            secondaryVendorsRow.cloneNode(true),
            productsRow
          );
        } else {
          combined.appendChild(secondaryVendorsRow.cloneNode(true));
        }
      } else if (primaryVendorsRow && secondaryVendorsRow) {
        const primaryList = primaryVendorsRow.querySelector('ul');
        const secondaryList = secondaryVendorsRow.querySelector('ul');

        if (primaryList && secondaryList) {
          const existing = new Set(
            [...primaryList.querySelectorAll('a')].map(a =>
              a.getAttribute('href')
            )
          );

          [...secondaryList.children].forEach(li => {
            const href = li.querySelector('a')?.getAttribute('href');
            if (!href || existing.has(href)) return;
            primaryList.appendChild(li.cloneNode(true));
            existing.add(href);
          });
        }
      }

      const combinedGrid = combined.querySelector('.predictive-search__grid');
      const secondaryGrid = secondarySection.querySelector(
        '.predictive-search__grid'
      );

      if (combinedGrid && secondaryGrid) {
        const getHref = el =>
          el.querySelector('a.card-product__content')?.getAttribute('href') ||
          el.querySelector('a.card-product__media')?.getAttribute('href');

        const existing = new Set(
          [...combinedGrid.children]
            .map(getHref)
            .filter(Boolean)
        );

        [...secondaryGrid.children].forEach(card => {
          if (combinedGrid.children.length >= 6) return;
          const href = getHref(card);
          if (!href || existing.has(href)) return;
          combinedGrid.appendChild(card.cloneNode(true));
          existing.add(href);
        });
      }

      return combined.innerHTML;
    }

    async getSearchResults(searchTerm) {
      try {
        const variants = this.getSearchTermVariants(searchTerm);
        const primaryTerm = variants[0];
        const secondaryTerm = variants.find(t => t !== primaryTerm);

        const primaryHtml = await this.fetchPredictiveSearchHtml(
          primaryTerm
        );

        if (!this.isOpen) {
          return;
        }

        let resultsMarkup = new DOMParser()
          .parseFromString(primaryHtml, 'text/html')
          .querySelector(
            '#shopify-section-predictive-search'
          ).innerHTML;

        if (secondaryTerm) {
          const secondaryHtml = await this.fetchPredictiveSearchHtml(
            secondaryTerm
          );
          if (this.isOpen) {
            resultsMarkup = this.mergeResults(
              primaryHtml,
              secondaryHtml
            );
          }
        }

        this.resultsContainer.innerHTML = resultsMarkup;
        this.open();
      } catch (error) {
        // Shopify's predictive search API answers 417 for ja / ko / zh-CN (unsupported): show only the
        // "Search for ..." row so Enter / the link still opens the full results page.
        const fallback = document.querySelector('template[data-bb-search-fallback]');
        if (error.message === '417' && fallback && this.isOpen) {
          const safeTerm = searchTerm
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
          this.resultsContainer.innerHTML = fallback.innerHTML.replace('__TERMS__', safeTerm);
          this.open();
          return;
        }

        this.errorMessageText.textContent = error.message;
        this.errorMessageContainer.classList.remove('hidden');
      }
    }

    open() {
      this.classList.add('is-open');
      this.resultsContainer.classList.remove('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.btnSubmit.setAttribute('tabindex', '-1');
      this.btnClearText.removeAttribute('disabled');
    }

    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      this.resultsContainer.classList.add('hidden');
      this.errorMessageContainer.classList.add('hidden');
      this.input.value = '';
      this.input.focus();
      this.btnSubmit.removeAttribute('tabindex');
      this.btnClearText.setAttribute('disabled', true);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
